import { sanitizeActions, type ReplyAction } from '../lib/reply-actions';
import { makeT, type WLang } from './i18n';

export async function streamChat(opts: {
  apiBase: string;
  tenantSlug: string;
  visitorId: string;
  conversationId: string | null;
  history: { role: 'user' | 'assistant'; content: string }[];
  message: string;
  lang?: WLang;
  /** 업체 대표 전화. 없으면 지연 안내에 번호를 붙이지 않는다. */
  phone?: string;
  onToken: (t: string) => void;
  /** 처리 단계(첫 글자 전) — 'intent'·'history'·'prepare'·'knowledge'·'availability'. `src/lib/chat-stages.ts`. */
  onStage?: (stage: string) => void;
  /** 서버가 후처리로 본문을 고쳤을 때 오는 전문 — 지금까지 받은 글을 이걸로 바꾼다. */
  onReplace?: (text: string) => void;
  onDone: (info: { conversationId: string; smalltalk: boolean; reservationUrl?: string; ctaLabel?: string; ctaForce?: boolean; bookingSummary?: string; messageId?: string; handoff?: { phone?: string; contactUrl?: string }; actions?: ReplyAction[] }) => void;
  onError: (msg: string) => void;
}) {
  const tr = makeT(opts.lang ?? 'ko');
  const controller = new AbortController();
  // 10초 안에 첫 토큰이 안 오면 타임아웃 → 사용자에게 안내 메시지
  const TIMEOUT_MS = 10_000;
  let gotFirstToken = false;
  const timer = setTimeout(() => {
    if (!gotFirstToken) {
      controller.abort();
      const call = opts.phone ? ` ${tr('streamTimeoutCall')} ${opts.phone}` : '';
      opts.onToken(tr('streamTimeout') + call);
      opts.onDone({ conversationId: opts.conversationId ?? '', smalltalk: false });
    }
  }, TIMEOUT_MS);

  try {
    const res = await fetch(`${opts.apiBase}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        tenantSlug: opts.tenantSlug,
        visitorId: opts.visitorId,
        ...(opts.conversationId != null ? { conversationId: opts.conversationId } : {}),
        history: opts.history,
        message: opts.message,
        // 처리 단계를 달라고 한다 — 서버는 첫 단계가 올 때 응답을 연다.
        stages: true,
      }),
    });
    if (!res.ok || !res.body) {
      clearTimeout(timer);
      opts.onToken(tr('streamError'));
      opts.onDone({ conversationId: opts.conversationId ?? '', smalltalk: false });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '', convId = opts.conversationId ?? '', smalltalk = false;
    let reservationUrl: string | undefined;
    let ctaLabel: string | undefined;
    // 답변에 URL 이 없어도 버튼을 붙일 턴(예약 가능 여부 질문 등) — 서버가 정한다.
    let ctaForce = false;
    let bookingSummary: string | undefined;
    let messageId: string | undefined;
    let handoff: { phone?: string; contactUrl?: string } | undefined;
    // 서버가 글을 다 만든 뒤 정한 행동 목록(없으면 meta 로 위젯이 직접 계산한다).
    let actions: ReplyAction[] | undefined;
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const events = buf.split('\n\n');
      buf = events.pop() ?? '';
      for (const ev of events) {
        const line = ev.trim().replace(/^data:\s*/, '');
        if (!line) continue;
        try {
          const obj = JSON.parse(line);
          if (obj.type === 'meta') {
            if (obj.conversationId) convId = obj.conversationId;
            if (obj.smalltalk) smalltalk = true;
            if (typeof obj.reservationUrl === 'string') reservationUrl = obj.reservationUrl;
            if (typeof obj.ctaLabel === 'string') ctaLabel = obj.ctaLabel;
            if (obj.ctaForce === true) ctaForce = true;
            if (typeof obj.bookingSummary === 'string') bookingSummary = obj.bookingSummary;
            if (typeof obj.assistantMessageId === 'string') messageId = obj.assistantMessageId;
            if (obj.handoff && typeof obj.handoff === 'object') handoff = { phone: obj.handoff.phone, contactUrl: obj.handoff.contactUrl };
          } else if (obj.type === 'stage' && typeof obj.stage === 'string') {
            if (!gotFirstToken) opts.onStage?.(obj.stage);
          } else if (obj.type === 'token') {
            if (!gotFirstToken) { gotFirstToken = true; clearTimeout(timer); }
            opts.onToken(obj.text);
          }
          else if (obj.type === 'replace' && typeof obj.text === 'string') opts.onReplace?.(obj.text);
          else if (obj.type === 'actions') actions = sanitizeActions(obj.actions);
          else if (obj.type === 'done') opts.onDone({ conversationId: convId, smalltalk, reservationUrl, ctaLabel, ctaForce, bookingSummary, messageId, handoff, actions });
        } catch { /* ignore malformed SSE lines */ }
      }
    }
    // 스트림이 끝났는데 done 이벤트를 못 받은 경우 안전 처리
    clearTimeout(timer);
  } catch (e) {
    clearTimeout(timer);
    if ((e as Error).name === 'AbortError') return; // 타임아웃 처리 완료
    opts.onToken(tr('streamError'));
    opts.onDone({ conversationId: opts.conversationId ?? '', smalltalk: false });
  }
}
